import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FolderKanban, Plus } from 'lucide-react';
import { CATEGORY_COLOURS, type CategoryColour } from '@fint/validation';
import { api, ApiError, qs } from '@/lib/api';
import { useActiveOrg } from '@/stores/auth';
import { usePermissions } from '@/hooks/usePermissions';
import { useEscapeToClose } from '@/hooks/useEscapeToClose';
import { useIsPhone } from '@/hooks/useIsPhone';
import { useBaseCurrency, useFiscalYearStartMonth } from '@/hooks/useOrgSettings';
import { PageHeader } from '@/app/DashboardLayout';
import { activeOnly } from '@/features/common/Drafts';
import { financialYearOf, financialYearPeriod, type Period } from '@/lib/dates';
import { Button, Card, CardFacts, CardLine, CardRow, Cards, ColourDot, ColourPicker, EmptyState, ErrorState, Input, Money, PeriodPicker, Sheet, SheetBody, SheetFooter, SheetForm, SheetHeader, SkeletonTable, Table, TBody, TD, Textarea, TH, THead, TR } from '@/features/common/ui';

interface ProjectRow {
  id: string;
  name: string;
  customerId: string | null;
  customerName: string | null;
  isInternal: boolean;
  status: 'active' | 'on_hold' | 'completed' | 'cancelled';
  colour: string;
  budgetAmount: string | null;
}

interface ReportRow {
  projectId: string | null;
  name: string;
  status: string | null;
  colour: string;
  customerName: string | null;
  isInternal: boolean;
  billed: string;
  costs: string;
  profit: string;
  invoiceCount: number;
  expenseCount: number;
}

interface ProjectReport {
  currency: string;
  from: string;
  to: string;
  caveats: string[];
  items: ReportRow[];
  unassigned: ReportRow;
  totals: { billed: string; costs: string; profit: string };
}

const STATUS_LABEL: Record<string, string> = {
  active: 'Active',
  on_hold: 'On hold',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

/**
 * "So we can see project-wise incomes and expenses" - this page.
 *
 * ── WHY THE HEADLINE COLUMN IS PROFIT, AND WHY IT IS THE ONLY SIGNED ONE ──
 * The row somebody opens this screen to find is the one losing money, so Profit
 * sorts first and carries a leading + or −. The SIGN is what conveys the
 * meaning; the colour only reinforces it, because roughly one man in twelve
 * cannot separate the two greens and reds this table uses.
 *
 * ── WHY "NOT ON A PROJECT" IS A ROW AND NOT A FOOTNOTE ──
 * A record carries at most one project, so the projects plus that row are the
 * business exactly - and printing it is what makes the arithmetic visibly
 * closed. It is also the thing that tells a new user their coverage is 23%,
 * without which they conclude the report is broken rather than incomplete.
 */
export function ProjectsPage() {
  const org = useActiveOrg();
  const perms = usePermissions();
  const isPhone = useIsPhone();
  const fyStart = useFiscalYearStartMonth();
  const queryClient = useQueryClient();

  const [period, setPeriod] = useState<Period>(() =>
    financialYearPeriod(financialYearOf(new Date().toISOString().slice(0, 10), fyStart).startYear, fyStart),
  );
  const [status, setStatus] = useState<'all' | ProjectRow['status']>('all');
  const [composerOpen, setComposerOpen] = useState(false);

  const orgId = org?.id;
  const currency = useBaseCurrency();

  const projects = useQuery({
    queryKey: ['projects', orgId, status],
    enabled: !!orgId,
    queryFn: () => api.get<{ items: ProjectRow[] }>(`/projects${qs({ status })}`),
  });

  // The money is a SEPARATE endpoint, gated on org-wide report access - a P&L
  // narrowed to one person is a different number, not a smaller one. So a
  // member who can put an expense on a project still gets the list; only the
  // figures are withheld, and the page says so rather than showing zeroes.
  const report = useQuery({
    queryKey: ['project-report', orgId, period.from, period.to],
    enabled: !!orgId && perms.seesAll('report'),
    queryFn: () =>
      api.get<ProjectReport>(`/reports/projects${qs({ from: period.from, to: period.to })}`),
  });

  // The unfiltered count, which is what decides whether this org has projects
  // at all - as opposed to whether any of them matches the pills.
  const total = projects.data?.items.length ?? 0;

  const rows = useMemo(() => {
    const byId = new Map((report.data?.items ?? []).map((row) => [row.projectId, row]));
    const listed = (projects.data?.items ?? []).filter(
      (project) => status === 'all' || project.status === status,
    );
    const merged = listed.map((project) => ({
      project,
      figures: byId.get(project.id),
    }));
    // Profit ascending: the project losing money is the first row, which is the
    // one this screen exists to surface.
    return merged.sort(
      (a, b) => Number(a.figures?.profit ?? 0) - Number(b.figures?.profit ?? 0),
    );
  }, [projects.data, report.data, status]);

  /**
   * The total is of the ROWS ON SCREEN, plus the unassigned row.
   *
   * It used to be the report's own grand total, which covers every project
   * whatever its status - so filtering to "Active" left a Total that did not
   * equal the column above it, and the arithmetic UX §6.7 calls "visibly
   * closed" was visibly open. A total that does not add up its own column is
   * worse than no total, because it is the figure a reader checks the others
   * against.
   */
  const shownTotals = useMemo(() => {
    const sum = (pick: (row: ReportRow) => string) => {
      const rowTotal = rows.reduce(
        (acc, entry) => acc + Math.round(Number(entry.figures ? pick(entry.figures) : 0) * 100),
        0,
      );
      const unassigned = report.data ? Math.round(Number(pick(report.data.unassigned)) * 100) : 0;
      return ((rowTotal + unassigned) / 100).toFixed(2);
    };
    return {
      billed: sum((row) => row.billed),
      costs: sum((row) => row.costs),
      profit: sum((row) => row.profit),
    };
  }, [rows, report.data]);

  const create = useMutation({
    mutationFn: (input: Record<string, unknown>) => api.post<ProjectRow>('/projects', input),
    onSuccess: () => {
      setComposerOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['projects', orgId] });
      void queryClient.invalidateQueries({ queryKey: ['project-report', orgId] });
    },
  });

  return (
    <>
      <PageHeader
        title="Projects"
        subtitle="What each piece of work brought in, and what it cost."
        actions={
          perms.can('project', 'create') ? (
            <Button onClick={() => setComposerOpen(true)}>
              <Plus className="h-4 w-4" /> New project
            </Button>
          ) : null
        }
      />
      {/* The same gutter every other screen has. Without it the content sat
          flush against the sidebar, on these four pages and no others. */}
      <div className="space-y-4 px-4 pb-10 sm:px-5">
        <div className="flex flex-wrap items-center gap-3">
          <PeriodPicker value={period} onChange={setPeriod} />
          <div className="flex flex-wrap gap-1.5">
            {(['all', 'active', 'on_hold', 'completed'] as const).map((key) => (
              <button
                key={key}
                type="button"
                aria-pressed={status === key}
                onClick={() => setStatus(key)}
                className={
                  status === key
                    ? 'rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary'
                    : 'rounded-full px-3 py-1 text-xs font-medium text-muted-foreground hover:bg-muted'
                }
              >
                {key === 'all' ? 'All' : STATUS_LABEL[key]}
              </button>
            ))}
          </div>
        </div>

        {projects.isPending ? <SkeletonTable rows={5} /> : null}
        {projects.isError ? (
          <ErrorState error={projects.error} onRetry={() => { void projects.refetch(); }} />
        ) : null}

        {/*
          ── TWO DIFFERENT EMPTY STATES, BECAUSE THEY ARE TWO DIFFERENT FACTS ──
          "You have no projects" and "none of your projects matches this filter"
          are not the same sentence, and showing the first when the second is true
          tells somebody with four active projects that their data is gone. The
          test is the UNFILTERED count; the filtered one only decides which
          message.
        */}
        {projects.isSuccess && total === 0 ? (
          <EmptyState
            icon={FolderKanban}
            title="Nothing on a project yet"
            description="Put an invoice or an expense on a project and this page shows what that piece of work earned and what it cost."
            action={
              perms.can('project', 'create') ? (
                <Button onClick={() => setComposerOpen(true)}>Create a project</Button>
              ) : null
            }
          />
        ) : null}

        {projects.isSuccess && total > 0 && rows.length === 0 ? (
          <EmptyState
            icon={FolderKanban}
            title="No projects with that status"
            description={`There ${total === 1 ? 'is' : 'are'} ${total} project${total === 1 ? '' : 's'}, but none of them is ${STATUS_LABEL[status]?.toLowerCase() ?? status}.`}
            action={<Button onClick={() => setStatus('all')}>Show them all</Button>}
          />
        ) : null}

        {projects.isSuccess && rows.length > 0 ? (
          isPhone ? (
            <Cards>
              {rows.map(({ project, figures }) => (
                <CardRow key={project.id}>
                  <CardLine
                    title={project.name}
                    meta={project.isInternal ? 'Internal' : (project.customerName ?? '—')}
                    value={<SignedProfit value={figures?.profit ?? '0'} currency={currency} />}
                  />
                  <CardFacts>
                    <span>{STATUS_LABEL[project.status]}</span>
                    <span>
                      Invoiced <Money value={figures?.billed ?? '0'} currency={currency} />
                    </span>
                    <span>
                      Costs <Money value={figures?.costs ?? '0'} currency={currency} />
                    </span>
                  </CardFacts>
                </CardRow>
              ))}
            </Cards>
          ) : (
            <Card>
              <Table>
                <THead>
                  <TR>
                    <TH>Project</TH>
                    <TH>Customer</TH>
                    <TH>Status</TH>
                    <TH className="text-right">Invoiced</TH>
                    <TH className="text-right">Costs</TH>
                    <TH className="text-right">Profit</TH>
                  </TR>
                </THead>
                <TBody>
                  {rows.map(({ project, figures }) => (
                    <TR key={project.id}>
                      <TD>
                        <span className="flex items-center gap-1.5">
                          <ColourDot colour={project.colour} />
                          {project.name}
                        </span>
                      </TD>
                      <TD className="text-muted-foreground">
                        {project.isInternal ? 'Internal' : (project.customerName ?? '—')}
                      </TD>
                      <TD className="text-muted-foreground">{STATUS_LABEL[project.status]}</TD>
                      <TD className="text-right tabular-nums">
                        <Money value={figures?.billed ?? '0'} currency={currency} showSymbol={false} />
                      </TD>
                      <TD className="text-right tabular-nums">
                        <Money value={figures?.costs ?? '0'} currency={currency} showSymbol={false} />
                      </TD>
                      <TD className="text-right tabular-nums">
                        <SignedProfit value={figures?.profit ?? '0'} currency={currency} />
                      </TD>
                    </TR>
                  ))}

                  {report.data ? (
                    <>
                      <TR className="bg-muted/40">
                        <TD colSpan={3}>Not on a project</TD>
                        <TD className="text-right tabular-nums">
                          <Money value={report.data.unassigned.billed} currency={currency} showSymbol={false} />
                        </TD>
                        <TD className="text-right tabular-nums">
                          <Money value={report.data.unassigned.costs} currency={currency} showSymbol={false} />
                        </TD>
                        <TD className="text-right tabular-nums">
                          <SignedProfit value={report.data.unassigned.profit} currency={currency} />
                        </TD>
                      </TR>
                      <TR className="font-medium">
                        <TD colSpan={3}>{status === 'all' ? 'Total' : 'Total of what is shown'}</TD>
                        <TD className="text-right tabular-nums">
                          <Money value={shownTotals.billed} currency={currency} showSymbol={false} />
                        </TD>
                        <TD className="text-right tabular-nums">
                          <Money value={shownTotals.costs} currency={currency} showSymbol={false} />
                        </TD>
                        <TD className="text-right tabular-nums">
                          <SignedProfit value={shownTotals.profit} currency={currency} />
                        </TD>
                      </TR>
                    </>
                  ) : null}
                </TBody>
              </Table>
            </Card>
          )
        ) : null}

        {/*
          The caveats sit under the table permanently rather than behind a
          tooltip. The single biggest way a profitability report loses trust is by
          not saying what it counted.
        */}
        {report.data ? (
          <p className="text-xs text-muted-foreground">
            {report.data.caveats.join(' ')}
          </p>
        ) : null}
        {!perms.seesAll('report') ? (
          <p className="text-xs text-muted-foreground">
            The money on this page covers the whole book, and your access is limited to your own
            records - so it is not shown. Ask an administrator if you need it.
          </p>
        ) : null}

        {composerOpen ? (
          <ProjectComposer
            onClose={() => setComposerOpen(false)}
            onSubmit={(input) => create.mutate(input)}
            pending={create.isPending}
            error={create.error instanceof ApiError ? create.error : undefined}
          />
        ) : null}
      </div>
    </>
  );
}

/**
 * Profit, with its sign spelled out.
 *
 * The `+`/`−` is the carrier of meaning and the colour reinforces it, never the
 * other way round. `−` is U+2212, not a hyphen: a hyphen at this size reads as
 * a dash and disappears at the left edge of a right-aligned column.
 */
function SignedProfit({ value, currency }: { value: string; currency: string }) {
  const negative = Number(value) < 0;
  const magnitude = negative ? value.replace('-', '') : value;
  return (
    <span className={negative ? 'text-destructive' : 'text-success'}>
      {negative ? '−' : '+'}
      <Money value={magnitude} currency={currency} showSymbol={false} />
    </span>
  );
}

function ProjectComposer({
  onClose,
  onSubmit,
  pending,
  error,
}: {
  onClose: () => void;
  onSubmit: (input: Record<string, unknown>) => void;
  pending: boolean;
  error?: ApiError;
}) {
  useEscapeToClose(onClose);
  const org = useActiveOrg();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [colour, setColour] = useState<CategoryColour>(CATEGORY_COLOURS[1]);

  const customers = useQuery({
    queryKey: ['customers', org?.id, 'project-picker'],
    enabled: !!org?.id,
    queryFn: () => api.get<{ items: Array<{ id: string; displayName: string }> }>('/customers?status=active&limit=200').then(activeOnly),
  });

  return (
    <Sheet labelledBy="project-sheet-title" onClose={onClose}>
      <SheetHeader titleId="project-sheet-title" title="New project" onClose={onClose} />
      <SheetForm
        onSubmit={(event) => {
          event.preventDefault();
          onSubmit({
            name,
            description: description || undefined,
            // An empty select is null, which MEANS internal - it is not a
            // missing value to be filled in later.
            customerId: customerId || null,
            colour,
          });
        }}
      >
        <SheetBody>
          <div className="space-y-1">
            {/* The asterisk sits OUTSIDE the label, or the accessible name
                becomes "Name star". */}
            <label htmlFor="project-name" className="text-sm font-medium">
              Name
            </label>
            <span aria-hidden="true" className="text-destructive"> *</span>
            <Input
              id="project-name"
              // The sheet opens for one purpose; landing focus anywhere else
              // makes a keyboard user tab past a heading and a close button to
              // reach the only field that matters.
              autoFocus
              value={name}
              onChange={(event) => setName(event.target.value)}
              required
              maxLength={200}
            />
          </div>

          <div className="space-y-1">
            <label htmlFor="project-customer" className="text-sm font-medium">
              Customer
            </label>
            <select
              id="project-customer"
              value={customerId}
              onChange={(event) => setCustomerId(event.target.value)}
              aria-describedby="project-customer-hint"
              className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
            >
              <option value="">No customer - an internal project</option>
              {(customers.data?.items ?? []).map((customer) => (
                <option key={customer.id} value={customer.id}>
                  {customer.displayName}
                </option>
              ))}
            </select>
            <p id="project-customer-hint" className="text-xs text-muted-foreground">
              An internal project - an office move, a product build - has costs and no customer. It can
              carry expenses but not invoices.
            </p>
          </div>

          <div className="space-y-1">
            <label htmlFor="project-notes" className="text-sm font-medium">
              Description
            </label>
            <Textarea
              id="project-notes"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              rows={3}
            />
          </div>

          <div className="space-y-1.5">
            <span className="text-sm font-medium">Colour</span>
            <ColourPicker value={colour} onChange={setColour} />
          </div>

          {error ? (
            // Announced. The 409 "that tag is on 47 records" is the whole reason
            // the button did nothing, and it renders below the fold of a form.
            <p role="alert" className="text-sm text-destructive">
              {error.message}
            </p>
          ) : null}
        </SheetBody>
        <SheetFooter>
          <Button type="submit" disabled={pending || !name.trim()}>
            {pending ? 'Creating…' : 'Create project'}
          </Button>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
        </SheetFooter>
      </SheetForm>
    </Sheet>
  );
}
